import { parseListType } from '../helpers/lists';
import { DjangoPixKey, parsePixKey, PixKey } from '../pix';
import { Address, DjangoAddress, parseAddress } from './../address';
import { BankAccount, DjangoBankAccount, parseBankAccount } from './../bank';
import { applyTimezone, parseDate } from './../helpers/date';
import { DjangoUserFile, parseUserFile, UserFile } from './user-file.model';
import { ILF_FINANCIAL_STATUS } from './user-financial-status.enum';

export class User {
  address = new Address();
  dateOfBirth: Date;
  email: string;
  emailConfirmed?: boolean;
  fullName: string;
  firstName: string;
  nickname: string;
  id: string;
  rating: number;
  isActive: boolean;
  isStaff: boolean;
  isEmployerStaff: boolean;
  ilfScore: number;
  ilfScoreResult: ILF_FINANCIAL_STATUS;
  ilfExpenses: number;
  ilfSavings: number;
  ilfDebts: number;
  ilfPlanning: number;
  phoneNumber: string;
  phoneNumberConfirmed?: boolean;
  socialSecurityNumber: string;
  identificationCard: string;
  identificationCardIssuerName: string;
  identificationCardIssuerState: string;
  termsAcceptedVersion: string;
  validated: boolean;
  limit: number;
  warnChangePassword: boolean;
  bankAccount: BankAccount;
  latestIlfFile: UserFile;
  files: UserFile[];
  pix: PixKey;

  constructor(data: string | User | DjangoUser = {}) {
    if (typeof data === 'string') {
      bindConstructorDataToInstance(this, { id: data });
    } else {
      bindConstructorDataToInstance(this, data);
    }
  }

  get djangoModel() {
    return getDjangoModel(this);
  }

  get basicInfoFilled() {
    return isBasicInfoFilled(this);
  }
}

export function parseUser(data?: string | User | DjangoUser) {
  if (data) {
    return new User(data);
  } else {
    return undefined;
  }
}

function isBasicInfoFilled(user: User) {
  return (
    !!user.fullName &&
    !!user.phoneNumber &&
    !!user.identificationCard &&
    !!user.identificationCardIssuerName &&
    !!user.identificationCardIssuerState &&
    user.address.basicInfoFilled
  );
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////

export interface DjangoUser {
  address?: DjangoAddress | string;
  date_of_birth?: string;
  email?: string;
  email_confirmed?: boolean;
  full_name?: string;
  first_name?: string;
  nickname?: string;
  id?: string;
  rating?: number;
  is_active?: boolean;
  is_staff?: boolean;
  is_customer_staff?: boolean;
  ilf_score?: number;
  ilf_score_result?: ILF_FINANCIAL_STATUS;
  ilf_expenses?: number;
  ilf_savings?: number;
  ilf_debts?: number;
  ilf_planning?: number;
  phone_number?: string;
  phone_number_confirmed?: boolean;
  social_security_number?: string;
  identification_card?: string;
  identification_card_issuer_name?: string;
  identification_card_issuer_state?: string;
  terms_accepted_version?: string;
  validated?: boolean;
  limit?: number;
  warn_change_password?: boolean;
  bank_account?: string | DjangoBankAccount;
  latest_ilf_file?: DjangoUserFile;
  files?: DjangoUserFile[];
  pix_data?: DjangoPixKey;
}

///////////////
/// PRIVATE ///
///////////////
function bindConstructorDataToInstance(instance: User, data: User | DjangoUser) {
  const jsData = bindDjangoPropsToJs(data);
  instance.address = parseAddress(jsData.address || {});
  instance.dateOfBirth = applyTimezone(parseDate(jsData.dateOfBirth), 0);
  instance.email = jsData.email;
  instance.emailConfirmed = jsData.emailConfirmed;
  instance.fullName = jsData.fullName;
  instance.firstName = jsData.firstName;
  instance.nickname = jsData.nickname;
  instance.id = jsData.id;
  instance.rating = jsData.rating;
  instance.isActive = jsData.isActive;
  instance.isStaff = jsData.isStaff;
  instance.isEmployerStaff = jsData.isEmployerStaff;
  instance.ilfScore = jsData.ilfScore;
  instance.ilfScoreResult = jsData.ilfScoreResult;
  instance.ilfExpenses = jsData.ilfExpenses;
  instance.ilfSavings = jsData.ilfSavings;
  instance.ilfDebts = jsData.ilfDebts;
  instance.ilfPlanning = jsData.ilfPlanning;
  instance.phoneNumber = jsData.phoneNumber;
  instance.phoneNumberConfirmed = jsData.phoneNumberConfirmed;
  instance.socialSecurityNumber = jsData.socialSecurityNumber;
  instance.identificationCard = jsData.identificationCard;
  instance.identificationCardIssuerName = jsData.identificationCardIssuerName;
  instance.identificationCardIssuerState = jsData.identificationCardIssuerState;
  instance.termsAcceptedVersion = jsData.termsAcceptedVersion;
  instance.validated = jsData.validated;
  instance.bankAccount = parseBankAccount(jsData.bankAccount);
  instance.warnChangePassword = jsData.warnChangePassword;
  instance.latestIlfFile = parseUserFile(jsData.latestIlfFile);
  instance.files = parseListType(jsData.files, (item) => new UserFile(item));
  instance.pix = parsePixKey(jsData.pix);
}

function bindDjangoPropsToJs(data: User | DjangoUser) {
  const djangoData = data as DjangoUser;
  const jsData = data as User;
  return {
    ...data,
    dateOfBirth: djangoData.date_of_birth || jsData.dateOfBirth,
    emailConfirmed: djangoData.email_confirmed || jsData.emailConfirmed,
    fullName: djangoData.full_name || jsData.fullName,
    firstName: djangoData.first_name || jsData.firstName,
    nickname: djangoData.nickname || jsData.nickname,
    isActive: djangoData.is_active || jsData.isActive,
    isStaff: djangoData.is_staff || jsData.isStaff,
    isEmployerStaff: djangoData.is_customer_staff || jsData.isEmployerStaff,
    ilfScore: djangoData.ilf_score || jsData.ilfScore,
    ilfScoreResult: djangoData.ilf_score_result || jsData.ilfScoreResult,
    ilfExpenses: djangoData.ilf_expenses || jsData.ilfExpenses,
    ilfSavings: djangoData.ilf_savings || jsData.ilfSavings,
    ilfDebts: djangoData.ilf_debts || jsData.ilfDebts,
    ilfPlanning: djangoData.ilf_planning || jsData.ilfPlanning,
    phoneNumber: djangoData.phone_number || jsData.phoneNumber,
    phoneNumberConfirmed: djangoData.phone_number_confirmed || jsData.phoneNumberConfirmed,
    socialSecurityNumber: djangoData.social_security_number || jsData.socialSecurityNumber,
    identificationCard: djangoData.identification_card || jsData.identificationCard,
    identificationCardIssuerName:
      djangoData.identification_card_issuer_name || jsData.identificationCardIssuerName,
    identificationCardIssuerState:
      djangoData.identification_card_issuer_state || jsData.identificationCardIssuerState,
    termsAcceptedVersion: djangoData.terms_accepted_version || jsData.termsAcceptedVersion,
    bankAccount: djangoData.bank_account || jsData.bankAccount,
    warnChangePassword: djangoData.warn_change_password || jsData.warnChangePassword,
    latestIlfFile: djangoData.latest_ilf_file || jsData.latestIlfFile,
    pix: djangoData.pix_data || jsData.pix,
  };
}

function getDjangoModel(user: User) {
  return {
    address: user.address?.djangoModel,
    date_of_birth: `${user.dateOfBirth}`,
    email: user.email,
    email_confirmed: user.emailConfirmed,
    full_name: user.fullName,
    first_name: user.firstName,
    nickname: user.nickname,
    id: user.id,
    rating: user.rating,
    is_active: user.isActive,
    is_staff: user.isStaff,
    phone_number: user.phoneNumber,
    phone_number_confirmed: user.phoneNumberConfirmed,
    social_security_number: user.socialSecurityNumber,
    identification_card: user.identificationCard,
    identification_card_issuer_name: user.identificationCardIssuerName,
    identification_card_issuer_state: user.identificationCardIssuerState,
    terms_accepted: user.termsAcceptedVersion,
    terms_accepted_version: `${user.termsAcceptedVersion}`,
    warn_change_password: user.warnChangePassword,
    pix_data: user.pix,
  };
}
